import React, {memo} from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import MUIContainer from '@/components/MUI-Components/MUI-Container';
import SectionHeader from '@/components/Common/SectionHeader';
import LayersOutlinedIcon from '@mui/icons-material/LayersOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import MonitorHeartOutlinedIcon from '@mui/icons-material/MonitorHeartOutlined';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import {colors, fontSize, fontWeight, borderRadius, borderWidth, shadows} from '@/styles/tokens';

// ─── Module-level sx constants ───────────────────────────────────────────────
const pillarCardSx = {
    background: colors.background.default,
    border: `${borderWidth.thin} solid ${colors.neutral.borderLight}`,
    borderRadius: borderRadius.lg,
    p: 3,
    height: '100%',
    boxShadow: shadows.lg,
    transition: 'transform 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease',
    '&:hover': {
        borderColor: colors.primary.deep,
        boxShadow: shadows.lg,
        transform: 'scale(1.02)'
    }
} as const;

const pillarIconWrapSx = {
    width: 44,
    height: 44,
    borderRadius: borderRadius['10'],
    background: colors.primary.iconBg,
    color: colors.primary.deep,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    mb: 2
} as const;

const pillarTitleSx = {
    fontWeight: fontWeight.bold,
    fontSize: fontSize[18],
    color: colors.text.primary,
    mb: 1.5
} as const;

const pillarDescSx = {
    color: colors.text.primary,
    fontSize: fontSize[16]
} as const;

const frameworksCardSx = {
    background: colors.background.default,
    border: `${borderWidth.thin} solid ${colors.neutral.borderLight}`,
    borderRadius: borderRadius.xl,
    p: {xs: 3, md: 5},
    mt: 6,
    boxShadow: shadows.lg
} as const;

const frameworksHeadingSx = {
    fontWeight: fontWeight.bold,
    fontSize: fontSize[20],
    color: colors.text.primary,
    mb: 1
} as const;

const frameworksSubheadingSx = {
    color: colors.text.secondary,
    fontSize: fontSize[16],
    mb: 3
} as const;

const frameworkItemSx = {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 1.5,
    p: 2,
    borderRadius: borderRadius['10'],
    border: `${borderWidth.thin} solid ${colors.neutral.borderLight}`,
    transition: 'transform 0.2s ease, background 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease',
    '&:hover': {
        background: colors.background.tag,
        borderColor: colors.primary.deep,
        boxShadow: shadows.lg,
        transform: 'scale(1.02)'
    }
} as const;

const frameworkIconSx = {
    fontSize: fontSize[16],
    color: colors.primary.main,
    flexShrink: 0,
    mt: '2px'
} as const;

const frameworkAbbrSx = {
    fontSize: fontSize[14]
} as const;

const frameworkNameSx = {
    fontSize: fontSize[14],
    mt: '2px'
} as const;
// ─────────────────────────────────────────────────────────────────────────────

const pillarIcons: Record<string, React.ReactNode> = {
    layers: <LayersOutlinedIcon />,
    evidence: <DescriptionOutlinedIcon />,
    monitor: <MonitorHeartOutlinedIcon />
};

interface Pillar {
    icon: string;
    title: string;
    description: string;
}

interface Framework {
    abbr: string;
    name: string;
}

interface ComplianceStandardsProps {
    eyebrow?: string;
    heading: string;
    subheading: string;
    pillars: Pillar[];
    frameworksHeading: string;
    frameworksSubheading: string;
    frameworks: Framework[];
}

const ComplianceStandards = ({
    eyebrow,
    heading,
    subheading,
    pillars,
    frameworksHeading,
    frameworksSubheading,
    frameworks
}: ComplianceStandardsProps) => {
    return (
        <MUIContainer>
            <SectionHeader
                eyebrow={eyebrow}
                heading={heading}
                subheading={subheading}
                subheadingVariant="body1"
                subheadingClassName="generative-transforming-subheading"
            />
            <Grid container spacing={3}>
                {pillars.map((pillar, index) => (
                    <Grid item xs={12} md={4} key={index}>
                        <Box sx={pillarCardSx}>
                            <Box sx={pillarIconWrapSx}>{pillarIcons[pillar.icon] ?? <LayersOutlinedIcon />}</Box>
                            <Typography variant="h3" sx={pillarTitleSx} className="font-headers">
                                {pillar.title}
                            </Typography>
                            <Typography component="p" sx={pillarDescSx} className="font-regular-text">
                                {pillar.description}
                            </Typography>
                        </Box>
                    </Grid>
                ))}
            </Grid>

            {/* Supported regulatory frameworks */}
            <Box sx={frameworksCardSx}>
                <Typography variant="h3" sx={frameworksHeadingSx} className="font-headers">
                    {frameworksHeading}
                </Typography>
                <Typography component="p" sx={frameworksSubheadingSx} className="font-regular-text">
                    {frameworksSubheading}
                </Typography>

                <Grid container spacing={2}>
                    {frameworks.map((framework, index) => (
                        <Grid item xs={12} sm={6} md={4} key={index}>
                            <Box sx={frameworkItemSx}>
                                <TaskAltIcon sx={frameworkIconSx} />
                                <Box>
                                    <Typography component="p" sx={frameworkAbbrSx} className="font-headers">
                                        {framework.abbr}
                                    </Typography>
                                    <Typography component="p" sx={frameworkNameSx} className="font-regular-text">
                                        {framework.name}
                                    </Typography>
                                </Box>
                            </Box>
                        </Grid>
                    ))}
                </Grid>
            </Box>
        </MUIContainer>
    );
};

export default memo(ComplianceStandards);
